import { useQuery, useMutation } from "@tanstack/react-query";
import { formatLocalDate } from "@/lib/utils";
import type { UserProfile } from "@shared/schema";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { VoiceToTextTextarea as Textarea, VoiceToTextButton } from "@/components/voice-field";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Loader2, DollarSign, AlertTriangle, CheckCircle, CheckCircle2, Clock, ArrowUpDown, Pencil, Mail, CreditCard, Phone, Trash2, Edit, Search, X, Send, FileText, Receipt, Percent, TrendingUp } from "lucide-react";
import { RichTextEditor, type RichTextEditorHandle } from "@/components/rich-text-editor";
import { CcEmailInput } from "@/components/cc-email-input";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { useState, useMemo, useRef, useEffect, useCallback } from "react";
import { useToast } from "@/hooks/use-toast";
import { apiRequest, queryClient } from "@/lib/queryClient";
import { cn } from "@/lib/utils";
import { describeSendError } from "@/lib/send-guard";
import { useSendEmailWithPreflight } from "@/hooks/use-pending-email-drafts";
import { useEffectivePermissions } from "@/hooks/use-effective-permissions";
import type { Invoice, Client, UserRole } from "@shared/schema";
import { AccountsPayableTab } from "@/components/billing/accounts-payable-tab";
import { AccountsReceivableTab } from "@/components/billing/accounts-receivable-tab";
import { CommissionsTab } from "@/components/billing/commissions-tab";
import { BalanceSheetTab } from "@/components/billing/balance-sheet-tab";
import { CashFlowTab } from "@/components/billing/cash-flow-tab";
import { TalentBillingRollupCard } from "@/components/admin/talent-billing-rollup-card";
import { Sparkles, BarChart2 } from "lucide-react";
import { DaysToPayTab } from "@/components/billing/days-to-pay-tab";
import { BillingIntelligenceTab } from "@/components/billing/billing-intelligence-tab";
import { PaymentEmailModal } from "@/components/payment-email-modal";
import { ScheduledEmailDialog } from "@/components/scheduled-email-dialog";
import { EmailAttachmentsField, type StagedAttachment } from "@/components/email-attachments-field";
import { ExpandableText } from "@/components/ui/expandable-text";
import { Handshake } from "lucide-react";
import { useSponsorHubAccess } from "@/hooks/use-sponsor-hub-access";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import { ChevronDown } from "lucide-react";

interface BillingPageProps {
  effectiveRole?: UserRole;
}

type SortField = "delinquency" | "invoiceNumber" | "accountName" | "eventRate" | "dateOfEvent" | "lateFee" | "paid";
type SortDirection = "asc" | "desc";

interface InvoiceWithClient extends Invoice {
  clientEmail?: string;
  clientPhone?: string;
}

function StripeIssuesTab() {
  const { data, isLoading } = useQuery<{ disputes: any[]; refunds: any[] }>({
    queryKey: ["/api/reports/stripe-issues"],
  });
  const dollars = (c: number) => `$${((c || 0) / 100).toFixed(2)}`;

  // Render the evidence-due deadline with a color cue. Stripe gives reps
  // ~7 days to respond and most card networks won't allow re-uploads after
  // the deadline, so we make sure overdue and ≤3-day-out disputes are
  // visually unmistakable.
  const renderEvidenceDue = (d: any) => {
    if (d.status && d.status !== 'warning_needs_response' && d.status !== 'needs_response') {
      // Once it's under_review / won / lost, the deadline is moot.
      return <span className="text-muted-foreground">—</span>;
    }
    if (!d.evidenceDueAt) {
      return <span className="text-muted-foreground">—</span>;
    }
    const due = new Date(d.evidenceDueAt);
    const days = typeof d.daysUntilEvidenceDue === 'number' ? d.daysUntilEvidenceDue : null;
    const overdue = !!d.isEvidenceOverdue || (days != null && days < 0);
    const urgent = !overdue && days != null && days <= 3;
    const cls = overdue
      ? "text-destructive font-semibold"
      : urgent
        ? "text-amber-600 dark:text-amber-400 font-medium"
        : "";
    const label = overdue
      ? `Overdue (${Math.abs(days ?? 0)}d ago)`
      : urgent
        ? `Due in ${days}d`
        : days != null
          ? `${days}d left`
          : '';
    return (
      <div className={cls} data-testid={`text-evidence-due-${d.id}`}>
        <div>{due.toLocaleDateString()}</div>
        {label && <div className="text-xs">{label}</div>}
      </div>
    );
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-8">
        <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
      </div>
    );
  }

  // Surface the count of disputes that still need a response so the rep
  // sees the urgency at a glance even before scrolling the table.
  const needsResponseCount = (data?.disputes || []).filter(
    (d: any) => d.status === 'warning_needs_response' || d.status === 'needs_response',
  ).length;
  const overdueCount = (data?.disputes || []).filter((d: any) => d.isEvidenceOverdue).length;

  return (
    <div className="space-y-6" data-testid="section-stripe-issues">
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <AlertTriangle className="h-5 w-5 text-destructive" /> Stripe Disputes
            {needsResponseCount > 0 && (
              <Badge variant={overdueCount > 0 ? "destructive" : "secondary"} data-testid="badge-needs-response">
                {needsResponseCount} need{needsResponseCount === 1 ? 's' : ''} response
                {overdueCount > 0 ? ` · ${overdueCount} overdue` : ''}
              </Badge>
            )}
          </CardTitle>
          <CardDescription>
            Active and historical chargebacks delivered by Stripe webhooks. Sorted by evidence-due date so the most urgent appear first.
          </CardDescription>
        </CardHeader>
        <CardContent>
          {(!data?.disputes || data.disputes.length === 0) ? (
            <p className="text-sm text-muted-foreground" data-testid="text-no-disputes">No disputes recorded.</p>
          ) : (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Dispute</TableHead>
                    <TableHead>Invoice</TableHead>
                    <TableHead>Reason</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead className="text-right">Amount</TableHead>
                    <TableHead>Evidence Due</TableHead>
                    <TableHead>Created</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {data.disputes.map((d: any) => (
                    <TableRow
                      key={d.id}
                      data-testid={`row-dispute-${d.id}`}
                      className={d.isEvidenceOverdue ? "bg-destructive/5" : ""}
                    >
                      <TableCell className="font-mono text-xs">{d.stripeDisputeId || d.id}</TableCell>
                      <TableCell>
                        {d.invoiceId ? (
                          <a
                            href={`/billing?invoice=${d.invoiceId}`}
                            className="text-primary hover:underline"
                            data-testid={`link-dispute-invoice-${d.id}`}
                          >
                            {d.invoiceNumber ? `#${d.invoiceNumber}` : 'View'}
                            {d.invoiceAccountName ? ` · ${d.invoiceAccountName}` : ''}
                          </a>
                        ) : (
                          <span className="text-muted-foreground">—</span>
                        )}
                      </TableCell>
                      <TableCell>{d.reason || "—"}</TableCell>
                      <TableCell>
                        <Badge
                          variant={
                            d.status === 'lost'
                              ? 'destructive'
                              : d.status === 'won'
                                ? 'default'
                                : (d.status === 'warning_needs_response' || d.status === 'needs_response')
                                  ? (d.isEvidenceOverdue ? 'destructive' : 'secondary')
                                  : 'outline'
                          }
                        >
                          {d.status || "open"}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-right">{dollars(d.amountCents)}</TableCell>
                      <TableCell>{renderEvidenceDue(d)}</TableCell>
                      <TableCell>{d.createdAt ? new Date(d.createdAt).toLocaleString() : "—"}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <DollarSign className="h-5 w-5" /> Stripe Refunds
          </CardTitle>
          <CardDescription>Refunds issued through Stripe.</CardDescription>
        </CardHeader>
        <CardContent>
          {(!data?.refunds || data.refunds.length === 0) ? (
            <p className="text-sm text-muted-foreground" data-testid="text-no-refunds">No refunds recorded.</p>
          ) : (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Refund</TableHead>
                    <TableHead>Invoice</TableHead>
                    <TableHead>Reason</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead className="text-right">Amount</TableHead>
                    <TableHead>Created</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {data.refunds.map((r: any) => (
                    <TableRow key={r.id} data-testid={`row-refund-${r.id}`}>
                      <TableCell className="font-mono text-xs">{r.stripeRefundId || r.id}</TableCell>
                      <TableCell>
                        {r.invoiceId ? (
                          <a
                            href={`/billing?invoice=${r.invoiceId}`}
                            className="text-primary hover:underline"
                            data-testid={`link-refund-invoice-${r.id}`}
                          >
                            {r.invoiceNumber ? `#${r.invoiceNumber}` : 'View'}
                            {r.invoiceAccountName ? ` · ${r.invoiceAccountName}` : ''}
                          </a>
                        ) : (
                          <span className="text-muted-foreground">—</span>
                        )}
                      </TableCell>
                      <TableCell>{r.reason || "—"}</TableCell>
                      <TableCell><Badge variant="outline">{r.status || "succeeded"}</Badge></TableCell>
                      <TableCell className="text-right">{dollars(r.amountCents)}</TableCell>
                      <TableCell>{r.createdAt ? new Date(r.createdAt).toLocaleString() : "—"}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

// Task #102 Phase 2 — Subscription invoices for THIS workspace. Reads
// from /api/account/subscription-invoices, which is bound to the active
// session workspace. Renders nothing on workspaces with no mirrored
// invoices so legacy tenants aren't affected.
function SubscriptionInvoicesSection() {
  const { data } = useQuery<{ invoices: any[] }>({
    queryKey: ["/api/account/subscription-invoices"],
    retry: false,
  });
  const invoices = data?.invoices || [];
  if (invoices.length === 0) return null;
  const fmt = (cents: number, ccy = "usd") =>
    `$${(cents / 100).toFixed(2)} ${ccy.toUpperCase()}`;
  return (
    <Card data-testid="card-subscription-invoices">
      <CardHeader>
        <CardTitle>Your Clarik subscription</CardTitle>
        <CardDescription>Invoices for your Clarik plan, mirrored from Stripe.</CardDescription>
      </CardHeader>
      <CardContent>
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Issued</TableHead>
              <TableHead>Amount</TableHead>
              <TableHead>Status</TableHead>
              <TableHead></TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {invoices.map((inv: any) => (
              <TableRow key={inv.id} data-testid={`row-sub-invoice-${inv.id}`}>
                <TableCell>{inv.issuedAt ? new Date(inv.issuedAt).toLocaleDateString() : "—"}</TableCell>
                <TableCell data-testid={`text-sub-amount-${inv.id}`}>{fmt(inv.amountDueCents || 0, inv.currency)}</TableCell>
                <TableCell><Badge variant={inv.status === "paid" ? "default" : "outline"}>{inv.status}</Badge></TableCell>
                <TableCell>
                  {inv.hostedInvoiceUrl ? (
                    <a href={inv.hostedInvoiceUrl} target="_blank" rel="noreferrer"
                       className="text-sm text-primary hover:underline"
                       data-testid={`link-sub-invoice-${inv.id}`}>View</a>
                  ) : null}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </CardContent>
    </Card>
  );
}

// Task #205 — Sponsor Hub invoice summary. Queries sponsor-tagged invoices
// and renders a collapsible card. Renders nothing when the feature is off.
function SponsorInvoicesSection() {
  const { data: access } = useSponsorHubAccess();
  const [open, setOpen] = useState(false);
  const currentYear = new Date().getFullYear();
  const [filterStatus, setFilterStatus] = useState<"all" | "paid" | "unpaid" | "overdue">("all");
  const [filterYear, setFilterYear] = useState<string>(String(currentYear));
  const years = Array.from({ length: 5 }, (_, i) => String(currentYear - i));

  const { data, isLoading } = useQuery<any[]>({
    queryKey: ["/api/sponsor-hub/invoices", filterStatus, filterYear],
    enabled: access?.enabled === true,
    queryFn: async () => {
      const params = new URLSearchParams();
      if (filterStatus !== "all") params.set("status", filterStatus);
      if (filterYear !== "all") params.set("year", filterYear);
      const res = await fetch(`/api/sponsor-hub/invoices?${params}`, { credentials: "include" });
      if (!res.ok) throw new Error("Failed");
      return res.json();
    },
  });

  if (!access?.enabled) return null;
  const invoices = data || [];

  const formatAmount = (dollars: number) =>
    new Intl.NumberFormat("en-US", { style: "currency", currency: "USD" }).format(dollars);

  return (
    <Collapsible open={open} onOpenChange={setOpen}>
      <Card>
        <CardHeader className="py-3 px-4">
          <div className="flex items-center justify-between">
            <CollapsibleTrigger asChild>
              <button className="flex items-center gap-2 text-sm font-medium hover:text-foreground/80 transition-colors" data-testid="button-sponsor-invoices-toggle">
                <Handshake className="h-4 w-4 text-muted-foreground" />
                Sponsor Invoices
                <Badge variant="secondary" className="ml-1">{isLoading ? "…" : invoices.length}</Badge>
                <ChevronDown className={`h-4 w-4 text-muted-foreground transition-transform ${open ? "rotate-180" : ""}`} />
              </button>
            </CollapsibleTrigger>
            <div className="flex items-center gap-2">
              <Select value={filterYear} onValueChange={setFilterYear}>
                <SelectTrigger className="h-7 w-[90px] text-xs" data-testid="select-sponsor-invoice-year">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All years</SelectItem>
                  {years.map((y) => <SelectItem key={y} value={y}>{y}</SelectItem>)}
                </SelectContent>
              </Select>
              <Select value={filterStatus} onValueChange={(v) => setFilterStatus(v as typeof filterStatus)}>
                <SelectTrigger className="h-7 w-[100px] text-xs" data-testid="select-sponsor-invoice-status">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All statuses</SelectItem>
                  <SelectItem value="paid">Paid</SelectItem>
                  <SelectItem value="unpaid">Unpaid</SelectItem>
                  <SelectItem value="overdue">Overdue</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
        </CardHeader>
        <CollapsibleContent>
          <CardContent className="pt-0 px-4 pb-4">
            {isLoading ? (
              <div className="flex justify-center py-4">
                <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
              </div>
            ) : invoices.length === 0 ? (
              <p className="text-sm text-muted-foreground py-2">No sponsor invoices for the selected filters.</p>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Invoice #</TableHead>
                    <TableHead>Account</TableHead>
                    <TableHead>Event Date</TableHead>
                    <TableHead>Amount</TableHead>
                    <TableHead>Status</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {invoices.map((inv: any) => (
                    <TableRow key={inv.id} data-testid={`row-sponsor-invoice-${inv.id}`}>
                      <TableCell className="font-mono text-xs">{inv.invoiceNumber ?? "—"}</TableCell>
                      <TableCell>{inv.accountName ?? "—"}</TableCell>
                      <TableCell>{inv.dateOfEvent ? new Date(inv.dateOfEvent).toLocaleDateString() : "—"}</TableCell>
                      <TableCell>{inv.eventRate != null ? formatAmount(Number(inv.eventRate)) : "—"}</TableCell>
                      <TableCell>
                        {inv.datePaid
                          ? <Badge className="text-xs bg-green-100 text-green-700 border-green-300">Paid</Badge>
                          : inv.dateOfEvent && new Date(inv.dateOfEvent) < new Date()
                          ? <Badge className="text-xs bg-red-100 text-red-700 border-red-300">Overdue</Badge>
                          : <Badge variant="outline" className="text-xs">{inv.status || "pending"}</Badge>
                        }
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </CardContent>
        </CollapsibleContent>
      </Card>
    </Collapsible>
  );
}

export default function BillingPage({ effectiveRole }: BillingPageProps) {
  const currentYear = new Date().getFullYear();
  // Initialise from URL params so Reports → Outstanding card deep-links here with year+filter pre-set
  const urlParams = new URLSearchParams(typeof window !== "undefined" ? window.location.search : "");
  const [selectedYear, setSelectedYear] = useState<string>(urlParams.get("year") || currentYear.toString());
  const [paidFilter, setPaidFilter] = useState<"all" | "unpaid" | "paid">((urlParams.get("paidFilter") as "all" | "unpaid" | "paid") || "all");
  const [overdueFilter, setOverdueFilter] = useState<string>(urlParams.get("overdueFilter") || "all");
  const [searchQuery, setSearchQuery] = useState("");
  const [visibleBillingCount, setVisibleBillingCount] = useState(100);
  const _billingObserverRef = useRef<IntersectionObserver | null>(null);
  const billingLoadMoreRef = useCallback((sentinel: HTMLTableRowElement | null) => {
    if (_billingObserverRef.current) {
      _billingObserverRef.current.disconnect();
      _billingObserverRef.current = null;
    }
    if (!sentinel) return;
    const observer = new IntersectionObserver(
      (entries) => { if (entries[0].isIntersecting) setVisibleBillingCount(prev => prev + 100); },
      { threshold: 0.1 }
    );
    observer.observe(sentinel);
    _billingObserverRef.current = observer;
  }, []);
  const [sortField, setSortField] = useState<SortField>("delinquency");
  const [sortDirection, setSortDirection] = useState<SortDirection>("desc");
  const [paymentDialogOpen, setPaymentDialogOpen] = useState(false);
  const [selectedInvoice, setSelectedInvoice] = useState<InvoiceWithClient | null>(null);
  const [selectedRowInvoice, setSelectedRowInvoice] = useState<InvoiceWithClient | null>(null);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [contactChangeConfirmOpen, setContactChangeConfirmOpen] = useState(false);
  const [datePaid, setDatePaid] = useState("");
  const [paymentType, setPaymentType] = useState("");
  const [paymentNotes, setPaymentNotes] = useState("");
  const [editForm, setEditForm] = useState({
    accountName: "",
    eventRate: "",
    billingNotes: "",
    bookingDetails: "",
    emailAddress: "",
    phoneNumber: "",
    status: "",
    datePaid: "",
  });
  const { can } = useEffectivePermissions();
  const canEditDatePaid = can("invoices.feature.editDatePaid");
  const [originalContactInfo, setOriginalContactInfo] = useState({
    emailAddress: "",
    phoneNumber: "",
  });
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [invoiceToDelete, setInvoiceToDelete] = useState<InvoiceWithClient | null>(null);
  const [selectedBillingIds, setSelectedBillingIds] = useState<Set<string>>(new Set());
  const [isBulkEditOpen, setIsBulkEditOpen] = useState(false);
  const [isBulkDeleteOpen, setIsBulkDeleteOpen] = useState(false);
  const [bulkEditField, setBulkEditField] = useState("");
  const [bulkEditValue, setBulkEditValue] = useState("");
  const [isEmailDialogOpen, setIsEmailDialogOpen] = useState(false);
  const [isEmailScheduleOpen, setIsEmailScheduleOpen] = useState(false);
  const [emailSubject, setEmailSubject] = useState("");
  const [emailBody, setEmailBody] = useState("");
  const [emailAttachments, setEmailAttachments] = useState<StagedAttachment[]>([]);
  const [emailCc, setEmailCc] = useState("");
  const [isLogCallDialogOpen, setIsLogCallDialogOpen] = useState(false);
  const [logCallNotes, setLogCallNotes] = useState("");
  const [isSetReminderDialogOpen, setIsSetReminderDialogOpen] = useState(false);
  const [reminderDate, setReminderDate] = useState("");
  const [reminderNote, setReminderNote] = useState("");
  const [isViewDetailsDialogOpen, setIsViewDetailsDialogOpen] = useState(false);
  const [activeTab, setActiveTab] = useState("audits");
  const { toast } = useToast();
  const pendingPaymentRef = useRef(false);
  const justPaidInvoiceRef = useRef<InvoiceWithClient | null>(null);
  const emailBodyEditorRef = useRef<RichTextEditorHandle>(null);
  const [paymentEmailModalOpen, setPaymentEmailModalOpen] = useState(false);
  const [justPaidInvoice, setJustPaidInvoice] = useState<InvoiceWithClient | null>(null);


  const { data: profile } = useQuery<UserProfile>({
    queryKey: ["/api/profile"],
  });
  const isAdmin = profile?.role === "admin";

  const { data: invoices = [], isLoading: invoicesLoading } = useQuery<Invoice[]>({
    queryKey: ["/api/invoices"],
  });

  const { data: clients = [], isLoading: clientsLoading } = useQuery<Client[]>({
    queryKey: ["/api/clients"],
  });

  const updateInvoiceMutation = useMutation({
    mutationFn: async ({ id, updates }: { id: string; updates: Partial<Invoice> }) => {
      pendingPaymentRef.current = updates.status === "paid";
      const response = await apiRequest("PATCH", `/api/invoices/${id}`, updates);
      return response.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/invoices"] });
      
      if (pendingPaymentRef.current) {
        toast({
          title: "Payment Recorded!",
          description: "Invoice has been marked as paid.",
        });
        // Capture invoice for email modal before dialog clears it
        const inv = justPaidInvoiceRef.current;
        justPaidInvoiceRef.current = null;
        closePaymentDialog();
        if (inv && (inv.clientEmail || inv.billingEmail)) {
          setJustPaidInvoice(inv);
          setPaymentEmailModalOpen(true);
        }
      } else {
        toast({
          title: "Invoice Updated",
          description: "Payment information has been saved.",
        });
        closePaymentDialog();
      }
      
      pendingPaymentRef.current = false;
    },
    onError: (error: Error) => {
      pendingPaymentRef.current = false;
      toast({
        title: "Update Failed",
        description: error.message || "Could not update invoice.",
        variant: "destructive",
      });
    },
  });

  const sendEmailViaPreflight = useSendEmailWithPreflight();
  const [sendingEmail, setSendingEmail] = useState(false);

  const sendEmailMutation = {
    isPending: sendingEmail,
    mutate: async ({ to, subject, body, cc, attachments }: { to: string; subject: string; body: string; cc?: string; attachments?: StagedAttachment[] }) => {
      setIsEmailDialogOpen(false);
      const recipient = to;
      const sub = subject;
      setEmailSubject("");
      setEmailBody("");
      setEmailCc("");
      setEmailAttachments([]);
      setSendingEmail(true);
      await sendEmailViaPreflight(
        {
          endpoint: "/api/email/send",
          payload: {
            to,
            subject,
            body,
            cc: cc || undefined,
            attachments: attachments && attachments.length > 0
              ? attachments.map((a) => ({ filename: a.filename, contentBase64: a.contentBase64, contentType: a.contentType }))
              : undefined,
          },
          recipientEmail: recipient,
          subject: sub,
          description: "billing email",
        },
        {
          onSent: () => {
            toast({ title: "Email sent", description: "Your email has been sent successfully." });
          },
          onError: (error: any) => {
            const { title, description } = describeSendError(error, "Failed to send email.");
            toast({ title, description, variant: "destructive" });
          },
        },
      );
      setSendingEmail(false);
    },
  } as const;

  const updateClientMutation = useMutation({
    mutationFn: async ({ id, updates }: { id: string; updates: Partial<Client> }) => {
      const response = await apiRequest("PATCH", `/api/clients/${id}`, updates);
      return response.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/clients"] });
    },
    onError: (error: Error) => {
      toast({
        title: "Update Failed",
        description: error.message || "Could not update client contact info.",
        variant: "destructive",
      });
    },
  });

  const deleteInvoiceMutation = useMutation({
    mutationFn: async (id: string) => {
      await apiRequest("DELETE", `/api/invoices/${id}`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/invoices"] });
      toast({
        title: "Invoice Deleted",
        description: "The invoice has been permanently removed.",
      });
      setDeleteConfirmOpen(false);
      setInvoiceToDelete(null);
    },
    onError: (error: Error) => {
      toast({
        title: "Delete Failed",
        description: error.message || "Could not delete invoice.",
        variant: "destructive",
      });
    },
  });

  const bulkEditMutation = useMutation({
    mutationFn: async ({ ids, field, value }: { ids: string[]; field: string; value: any }) => {
      const updates = ids.map(id => 
        apiRequest("PATCH", `/api/invoices/${id}`, { [field]: value })
      );
      return Promise.all(updates);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/invoices"] });
      setSelectedBillingIds(new Set());
      setIsBulkEditOpen(false);
      setBulkEditField("");
      setBulkEditValue("");
      toast({ title: "Invoices Updated", description: `Successfully updated ${selectedBillingIds.size} invoices.` });
    },
    onError: (error: any) => {
      toast({ title: "Error", description: error.message || "Failed to update invoices.", variant: "destructive" });
    },
  });

  const handleBulkEdit = () => {
    if (selectedBillingIds.size === 0 || !bulkEditField) return;
    bulkEditMutation.mutate({
      ids: Array.from(selectedBillingIds),
      field: bulkEditField,
      value: bulkEditValue,
    });
  };

  const bulkDeleteMutation = useMutation({
    mutationFn: async (ids: string[]) => {
      const deletes = ids.map(id => 
        apiRequest("DELETE", `/api/invoices/${id}`)
      );
      return Promise.all(deletes);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/invoices"] });
      const count = selectedBillingIds.size;
      setSelectedBillingIds(new Set());
      setIsBulkDeleteOpen(false);
      toast({ title: "Invoices Deleted", description: `Successfully deleted ${count} invoices.` });
    },
    onError: (error: any) => {
      toast({ title: "Error", description: error.message || "Failed to delete invoices.", variant: "destructive" });
    },
  });

  const handleBulkDelete = () => {
    if (selectedBillingIds.size === 0) return;
    bulkDeleteMutation.mutate(Array.from(selectedBillingIds));
  };

  // Invoice activity query (for view details)
  const { data: invoiceActivities = [], refetch: refetchActivities, isLoading: isLoadingActivities } = useQuery({
    queryKey: ["/api/invoices", selectedRowInvoice?.id, "activities"],
    queryFn: async () => {
      if (!selectedRowInvoice?.id) return [];
      const response = await fetch(`/api/invoices/${selectedRowInvoice.id}/activities`);
      if (!response.ok) return [];
      return response.json();
    },
    enabled: !!selectedRowInvoice?.id && isViewDetailsDialogOpen,
  });

  const createActivityMutation = useMutation({
    mutationFn: async ({ invoiceId, type, notes }: { invoiceId: string; type: string; notes: string }) => {
      return apiRequest("POST", `/api/invoices/${invoiceId}/activities`, { type, notes });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/invoices"] });
      refetchActivities();
      setIsLogCallDialogOpen(false);
      setLogCallNotes("");
      toast({ title: "Call Logged", description: "Your call notes have been saved." });
    },
    onError: (error: any) => {
      toast({ title: "Error", description: error.message || "Failed to log call.", variant: "destructive" });
    },
  });

  const createReminderMutation = useMutation({
    mutationFn: async (data: { invoiceId: string; dueDate: string; note: string }) => {
      const response = await apiRequest("POST", "/api/invoice-followups", data);
      return response.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/invoices"] });
      setIsSetReminderDialogOpen(false);
      setReminderDate("");
      setReminderNote("");
      toast({ title: "Reminder Set", description: "Your reminder has been saved." });
    },
    onError: (error: any) => {
      toast({ title: "Error", description: error.message || "Failed to set reminder.", variant: "destructive" });
    },
  });

  const handleSelectAllBilling = (checked: boolean) => {
    if (checked) {
      setSelectedBillingIds(new Set(filteredInvoices.map(inv => inv.id)));
    } else {
      setSelectedBillingIds(new Set());
    }
  };

  const handleSelectBilling = (invoiceId: string, checked: boolean) => {
    const newSet = new Set(selectedBillingIds);
    if (checked) {
      newSet.add(invoiceId);
    } else {
      newSet.delete(invoiceId);
    }
    setSelectedBillingIds(newSet);
  };

  const closePaymentDialog = () => {
    setPaymentDialogOpen(false);
    setSelectedInvoice(null);
    setDatePaid("");
    setPaymentType("");
    setPaymentNotes("");
  };

  const handlePaidCheckChange = (invoice: InvoiceWithClient, checked: boolean) => {
    if (checked) {
      setSelectedInvoice(invoice);
      setDatePaid(new Date().toISOString().split("T")[0]);
      setPaymentType("");
      setPaymentNotes("");
      setPaymentDialogOpen(true);
    } else {
      updateInvoiceMutation.mutate({
        id: invoice.id,
        updates: {
          status: "pending",
          datePaid: null,
          paymentType: null,
          paymentNotes: null,
        },
      });
    }
  };

  const handlePaymentSubmit = () => {
    if (!selectedInvoice || !datePaid || !paymentType) return;
    if (paymentType === "Other" && !paymentNotes.trim()) {
      toast({
        title: "Notes Required",
        description: "Please provide notes when selecting 'Other' payment type.",
        variant: "destructive",
      });
      return;
    }

    justPaidInvoiceRef.current = selectedInvoice;
    updateInvoiceMutation.mutate({
      id: selectedInvoice.id,
      updates: {
        status: "paid",
        datePaid: new Date(datePaid),
        paymentType,
        paymentNotes: paymentNotes.trim() || null,
      },
    });
  };

  const clientMap = useMemo(() => {
    const map = new Map<string, Client>();
    clients.forEach((client) => map.set(client.id, client));
    return map;
  }, [clients]);

  const invoicesWithClients: InvoiceWithClient[] = useMemo(() => {
    return invoices.map((invoice) => {
      const client = clientMap.get(invoice.clientId);
      return {
        ...invoice,
        clientEmail: client?.emailAddress || undefined,
        clientPhone: client?.phoneNumber || undefined,
      };
    });
  }, [invoices, clientMap]);

  const calculateDelinquency = (invoice: Invoice): number => {
    if (!invoice.dateOfEvent || invoice.status === "paid" || invoice.status === "cancelled") {
      return -Infinity;
    }
    const showDate = new Date(invoice.dateOfEvent);
    const today = new Date();
    const diffTime = today.getTime() - showDate.getTime();
    const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24));
    return diffDays;
  };

  const filteredInvoices = useMemo(() => {
    let result = invoicesWithClients
      .filter((inv) => inv.status !== "cancelled")
      .filter((inv) => {
        if (selectedYear === "all") return true;
        const showDate = inv.dateOfEvent ? new Date(inv.dateOfEvent) : null;
        if (!showDate) return true;
        return showDate.getFullYear() === parseInt(selectedYear);
      });
    
    // Apply paid/unpaid filter
    if (paidFilter === "unpaid") {
      result = result.filter(inv => inv.status !== "paid");
    } else if (paidFilter === "paid") {
      result = result.filter(inv => inv.status === "paid");
    }

    // Apply search filter
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase().trim();
      result = result.filter(inv => 
        inv.invoiceNumber?.toLowerCase().includes(query) ||
        inv.accountName?.toLowerCase().includes(query) ||
        inv.accountAddress?.toLowerCase().includes(query) ||
        inv.bookingDetails?.toLowerCase().includes(query) ||
        inv.rep?.toLowerCase().includes(query) ||
        inv.billingNotes?.toLowerCase().includes(query)
      );
    }
    
    // Apply overdue filter
    if (overdueFilter !== "all") {
      const minDays = parseInt(overdueFilter);
      result = result.filter(inv => {
        if (inv.status === "paid") return false;
        const delinquency = calculateDelinquency(inv);
        return delinquency >= minDays;
      });
    }
    
    return result.sort((a, b) => {
        let comparison = 0;
        switch (sortField) {
          case "delinquency":
            comparison = calculateDelinquency(b) - calculateDelinquency(a);
            break;
          case "invoiceNumber":
            comparison = (a.invoiceNumber || "").localeCompare(b.invoiceNumber || "");
            break;
          case "accountName":
            comparison = (a.accountName || "").localeCompare(b.accountName || "");
            break;
          case "eventRate":
            comparison = Number(a.eventRate || 0) - Number(b.eventRate || 0);
            break;
          case "dateOfEvent": {
            const dateA = a.dateOfEvent ? new Date(a.dateOfEvent).getTime() : 0;
            const dateB = b.dateOfEvent ? new Date(b.dateOfEvent).getTime() : 0;
            comparison = dateA - dateB;
            break;
          }
          case "lateFee":
            comparison = Number(a.lateFee || 0) - Number(b.lateFee || 0);
            break;
          case "paid": {
            const paidA = a.status === "paid" ? 1 : 0;
            const paidB = b.status === "paid" ? 1 : 0;
            comparison = paidA - paidB;
            break;
          }
        }
        return sortDirection === "desc" ? -comparison : comparison;
      });
  }, [invoicesWithClients, selectedYear, sortField, sortDirection, searchQuery, overdueFilter, paidFilter]);

  // Reset visible count when filters change (Fix B)
  useEffect(() => {
    setVisibleBillingCount(100);
  }, [selectedYear, sortField, sortDirection, searchQuery, overdueFilter, paidFilter, invoices]);


  const visibleBillingInvoices = filteredInvoices.slice(0, visibleBillingCount);
  const hasMoreBilling = visibleBillingCount < filteredInvoices.length;

  const handleSort = (field: SortField) => {
    if (sortField === field) {
      setSortDirection(sortDirection === "asc" ? "desc" : "asc");
    } else {
      setSortField(field);
      setSortDirection("desc");
    }
  };

  const formatDate = (date: Date | string | null) => formatLocalDate(date);

  const formatCurrency = (amount: string | number | null) => {
    if (!amount) return "$0";
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "USD",
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(Number(amount));
  };

  const getDelinquencyBadge = (invoice: Invoice) => {
    const days = calculateDelinquency(invoice);
    if (invoice.status === "paid") {
      return <Badge variant="default" className="bg-green-600">Paid</Badge>;
    }
    if (days === -Infinity || days < 0) {
      return <Badge variant="secondary">Upcoming</Badge>;
    }
    if (days <= 30) {
      return <Badge variant="secondary">{days} days</Badge>;
    }
    if (days <= 60) {
      return <Badge variant="outline" className="border-yellow-500 text-yellow-600">{days} days</Badge>;
    }
    return <Badge variant="destructive">{days} days overdue</Badge>;
  };

  const totalOutstanding = filteredInvoices
    .filter((inv) => inv.status !== "paid")
    .reduce((sum, inv) => sum + Number(inv.eventRate || 0), 0);

  const paidCount = filteredInvoices.filter((inv) => inv.status === "paid").length;
  const unpaidCount = filteredInvoices.filter((inv) => inv.status !== "paid").length;

  const isLoading = invoicesLoading || clientsLoading;

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-full">
        <Loader2 className="w-8 h-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  return (
    <div className="p-3 sm:p-6 space-y-4 sm:space-y-6">
      <div className="flex items-center justify-between gap-4 flex-wrap">
        <div>
          <h1 className="text-xl sm:text-2xl font-bold" data-testid="text-billing-title">Billing</h1>
          <p className="text-muted-foreground hidden sm:block">Manage payments, expenses, and commissions</p>
        </div>
      </div>

      {/* Task #102 Phase 2 — Clarik subscription invoices mirror for this
          workspace. Renders nothing on workspaces without any mirrored
          invoices, so legacy tenant workspaces are unaffected. */}
      <SubscriptionInvoicesSection />
      {/* Task #205 — Sponsor Hub invoices. Only shown when feature is enabled. */}
      <SponsorInvoicesSection />

      <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
        <TabsList className="grid w-full grid-cols-10 lg:w-[1360px]">
          <TabsTrigger value="audits" className="flex items-center gap-2" data-testid="tab-billing-audits">
            <FileText className="h-4 w-4" />
            <span className="hidden sm:inline">Audits</span>
          </TabsTrigger>
          <TabsTrigger value="payables" className="flex items-center gap-2" data-testid="tab-accounts-payable">
            <Receipt className="h-4 w-4" />
            <span className="hidden sm:inline">Payables</span>
          </TabsTrigger>
          <TabsTrigger value="receivables" className="flex items-center gap-2" data-testid="tab-accounts-receivable">
            <DollarSign className="h-4 w-4" />
            <span className="hidden sm:inline">Receivables</span>
          </TabsTrigger>
          <TabsTrigger value="commissions" className="flex items-center gap-2" data-testid="tab-commissions">
            <Percent className="h-4 w-4" />
            <span className="hidden sm:inline">Commissions</span>
          </TabsTrigger>
          <TabsTrigger value="balance-sheet" className="flex items-center gap-2" data-testid="tab-balance-sheet">
            <CreditCard className="h-4 w-4" />
            <span className="hidden sm:inline">Balance Sheet</span>
          </TabsTrigger>
          <TabsTrigger value="stripe-issues" className="flex items-center gap-2" data-testid="tab-stripe-issues">
            <AlertTriangle className="h-4 w-4" />
            <span className="hidden sm:inline">Disputes</span>
          </TabsTrigger>
          <TabsTrigger value="cash-flow" className="flex items-center gap-2" data-testid="tab-cash-flow">
            <TrendingUp className="h-4 w-4" />
            <span className="hidden sm:inline">Cash Flow</span>
          </TabsTrigger>
          <TabsTrigger value="days-to-pay" className="flex items-center gap-2" data-testid="tab-days-to-pay">
            <Clock className="h-4 w-4" />
            <span className="hidden sm:inline">Days to Pay</span>
          </TabsTrigger>
          <TabsTrigger value="talent-ai-billing" className="flex items-center gap-2" data-testid="tab-talent-ai-billing">
            <Sparkles className="h-4 w-4" />
            <span className="hidden sm:inline">Talent AI Billing</span>
          </TabsTrigger>
          <TabsTrigger value="intelligence" className="flex items-center gap-2" data-testid="tab-billing-intelligence">
            <BarChart2 className="h-4 w-4" />
            <span className="hidden sm:inline">Intelligence</span>
          </TabsTrigger>
        </TabsList>

        <TabsContent value="payables" className="mt-6">
          <AccountsPayableTab />
        </TabsContent>

        <TabsContent value="receivables" className="mt-6">
          <AccountsReceivableTab />
        </TabsContent>

        <TabsContent value="commissions" className="mt-6">
          <CommissionsTab />
        </TabsContent>

        <TabsContent value="balance-sheet" className="mt-6">
          <BalanceSheetTab />
        </TabsContent>

        <TabsContent value="stripe-issues" className="mt-6">
          <StripeIssuesTab />
        </TabsContent>

        <TabsContent value="cash-flow" className="mt-6">
          <CashFlowTab />
        </TabsContent>

        <TabsContent value="days-to-pay" className="mt-6">
          <DaysToPayTab />
        </TabsContent>

        <TabsContent value="talent-ai-billing" className="mt-6">
          <TalentBillingRollupCard />
        </TabsContent>

        <TabsContent value="intelligence" className="mt-6">
          <BillingIntelligenceTab />
        </TabsContent>

        <TabsContent value="audits" className="mt-6">
      <div className="flex items-center justify-end gap-3">
          {/* Search Bar */}
          <div className="flex items-center h-9 w-64 rounded-md border border-input bg-background px-3 focus-within:ring-2 focus-within:ring-ring focus-within:ring-offset-2">
            <Search className="h-4 w-4 text-muted-foreground shrink-0" />
            <input
              placeholder="Search accounts..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="flex-1 bg-transparent outline-none text-sm placeholder:text-muted-foreground ml-2 min-w-0 text-foreground"
              data-testid="input-search-billing"
            />
            {searchQuery && (
              <button
                className="shrink-0 ml-1 text-muted-foreground hover:text-foreground"
                onClick={() => setSearchQuery("")}
                data-testid="button-clear-search"
              >
                <X className="h-3 w-3" />
              </button>
            )}
          </div>
          
          <span className="text-sm text-muted-foreground">Year:</span>
          <Select value={selectedYear} onValueChange={setSelectedYear}>
            <SelectTrigger className="w-[120px]" data-testid="select-billing-year">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Years</SelectItem>
              <SelectItem value="2028">2028</SelectItem>
              <SelectItem value="2027">2027</SelectItem>
              <SelectItem value="2026">2026</SelectItem>
              <SelectItem value="2025">2025</SelectItem>
            </SelectContent>
          </Select>
        </div>

      <div className="grid gap-4 md:grid-cols-3">
        <Card
          className="cursor-pointer transition-all hover:ring-2 hover:ring-destructive/40"
          onClick={() => { setPaidFilter("unpaid"); setOverdueFilter("all"); }}
          data-testid="card-outstanding-balance"
        >
          <CardHeader className="flex flex-row items-center justify-between gap-2 space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Outstanding Balance</CardTitle>
            <DollarSign className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-destructive" data-testid="text-outstanding-balance">
              {formatCurrency(totalOutstanding)}
            </div>
            <p className="text-xs text-muted-foreground">{unpaidCount} unpaid invoices</p>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="flex flex-row items-center justify-between gap-2 space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Paid Invoices</CardTitle>
            <CheckCircle className="h-4 w-4 text-green-600" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-green-600" data-testid="text-paid-count">
              {paidCount}
            </div>
            <p className="text-xs text-muted-foreground">Completed payments</p>
          </CardContent>
        </Card>
        <Card
          className="cursor-pointer transition-all hover:ring-2 hover:ring-destructive/40"
          onClick={() => { setPaidFilter("unpaid"); setOverdueFilter("30"); setSortField("delinquency"); setSortDirection("desc"); }}
          data-testid="card-delinquent-accounts"
        >
          <CardHeader className="flex flex-row items-center justify-between gap-2 space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Delinquent Accounts</CardTitle>
            <AlertTriangle className="h-4 w-4 text-destructive" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold" data-testid="text-delinquent-count">
              {filteredInvoices.filter((inv) => calculateDelinquency(inv) > 30).length}
            </div>
            <p className="text-xs text-muted-foreground">Over 30 days past due</p>
          </CardContent>
        </Card>
      </div>

      <Card>
        <CardHeader className="flex flex-row items-center justify-between gap-4">
          <div className="flex items-center gap-4 flex-wrap">
            <CardTitle className="flex items-center gap-2">
              <Clock className="h-5 w-5" />
              Account Audit
            </CardTitle>
            <div className="flex items-center gap-2">
              <Select value={paidFilter} onValueChange={(v) => setPaidFilter(v as "all" | "unpaid" | "paid")}>
                <SelectTrigger className="w-[130px]" data-testid="select-paid-filter">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All statuses</SelectItem>
                  <SelectItem value="unpaid">Unpaid only</SelectItem>
                  <SelectItem value="paid">Paid only</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="flex items-center gap-2">
              <span className="text-sm text-muted-foreground whitespace-nowrap">Sorting option:</span>
              <Select value={overdueFilter} onValueChange={setOverdueFilter}>
                <SelectTrigger className="w-[160px]" data-testid="select-overdue-filter">
                  <SelectValue placeholder="All invoices" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All invoices</SelectItem>
                  <SelectItem value="30">30+ days overdue</SelectItem>
                  <SelectItem value="60">60+ days overdue</SelectItem>
                  <SelectItem value="90">90+ days overdue</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          {selectedRowInvoice && (
            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => setIsLogCallDialogOpen(true)}
                data-testid="button-log-call"
              >
                <Phone className="w-4 h-4 mr-2" />
                Log Call
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => {
                  if (selectedRowInvoice.clientEmail) {
                    setEmailSubject("");
                    setEmailBody("");
                    setIsEmailDialogOpen(true);
                  } else {
                    toast({
                      title: "No Email",
                      description: "This account doesn't have an email address.",
                      variant: "destructive",
                    });
                  }
                }}
                data-testid="button-email"
              >
                <Mail className="w-4 h-4 mr-2" />
                Email
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => setIsSetReminderDialogOpen(true)}
                data-testid="button-set-reminder"
              >
                <Clock className="w-4 h-4 mr-2" />
                Set Reminder
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => setIsViewDetailsDialogOpen(true)}
                data-testid="button-view-details"
              >
                <Search className="w-4 h-4 mr-2" />
                View Details
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => {
                  const email = selectedRowInvoice.clientEmail || "";
                  const phone = selectedRowInvoice.clientPhone || "";
                  setEditForm({
                    accountName: selectedRowInvoice.accountName || "",
                    eventRate: selectedRowInvoice.eventRate || "",
                    billingNotes: selectedRowInvoice.billingNotes || "",
                    bookingDetails: selectedRowInvoice.bookingDetails || "",
                    emailAddress: email,
                    phoneNumber: phone,
                    status: selectedRowInvoice.status || "pending",
                  });
                  setOriginalContactInfo({
                    emailAddress: email,
                    phoneNumber: phone,
                  });
                  setEditDialogOpen(true);
                }}
                data-testid="button-edit-record"
              >
                <Pencil className="w-4 h-4 mr-2" />
                Edit Record
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => {
                  if (selectedRowInvoice.status !== "paid") {
                    setSelectedInvoice(selectedRowInvoice);
                    setDatePaid(new Date().toISOString().split("T")[0]);
                    setPaymentType("");
                    setPaymentNotes("");
                    setPaymentDialogOpen(true);
                  }
                }}
                disabled={selectedRowInvoice.status === "paid"}
                data-testid="button-mark-paid"
              >
                <CreditCard className="w-4 h-4 mr-2" />
                Mark Paid
              </Button>
            </div>
          )}
        </CardHeader>
        <CardContent>
          {/* Bulk Edit Toolbar */}
          {isAdmin && selectedBillingIds.size > 0 && (
            <div className="flex items-center gap-2 p-3 mb-4 bg-muted/50 rounded-md">
              <span className="text-sm font-medium">{selectedBillingIds.size} selected</span>
              <Button
                variant="outline"
                size="sm"
                onClick={() => setIsBulkEditOpen(true)}
                data-testid="button-bulk-edit-billing"
              >
                <Edit className="w-4 h-4 mr-2" />
                Bulk Edit
              </Button>
              <Button
                variant="destructive"
                size="sm"
                onClick={() => setIsBulkDeleteOpen(true)}
                data-testid="button-bulk-delete-billing"
              >
                <Trash2 className="w-4 h-4 mr-2" />
                Delete Selected
              </Button>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setSelectedBillingIds(new Set())}
                data-testid="button-clear-selection-billing"
              >
                Clear Selection
              </Button>
            </div>
          )}

          {/* Bulk Delete Confirmation Dialog */}
          <AlertDialog open={isBulkDeleteOpen} onOpenChange={setIsBulkDeleteOpen}>
            <AlertDialogContent>
              <AlertDialogHeader>
                <AlertDialogTitle>Delete {selectedBillingIds.size} Invoices?</AlertDialogTitle>
                <AlertDialogDescription>
                  Are you sure you want to delete {selectedBillingIds.size} invoices? 
                  These invoices will be moved to deleted items and can be restored within 7 days.
                </AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel data-testid="button-cancel-bulk-delete-billing">Cancel</AlertDialogCancel>
                <AlertDialogAction
                  onClick={handleBulkDelete}
                  className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                  data-testid="button-confirm-bulk-delete-billing"
                >
                  {bulkDeleteMutation.isPending && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                  Delete {selectedBillingIds.size} Invoices
                </AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>

          {filteredInvoices.length === 0 ? (
            <div className="text-center py-8 text-muted-foreground">
              <DollarSign className="h-12 w-12 mx-auto mb-4 opacity-50" />
              <p>No invoices found for the selected year.</p>
            </div>
          ) : (
            <div className="rounded-md border overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    {isAdmin && (
                      <TableHead className="w-[40px]">
                        <Checkbox
                          checked={filteredInvoices.length > 0 && selectedBillingIds.size === filteredInvoices.length}
                          onCheckedChange={handleSelectAllBilling}
                          data-testid="checkbox-select-all-billing"
                        />
                      </TableHead>
                    )}
                    <TableHead>
                      <Button
                        variant="ghost"
                        size="sm"
                        className="h-8 p-0 font-medium"
                        onClick={() => handleSort("invoiceNumber")}
                        data-testid="button-sort-invoice"
                      >
                        Invoice #
                        <ArrowUpDown className="ml-1 h-3 w-3" />
                      </Button>
                    </TableHead>
                    <TableHead className="min-w-[180px]">
                      <Button
                        variant="ghost"
                        size="sm"
                        className="h-8 p-0 font-medium"
                        onClick={() => handleSort("accountName")}
                        data-testid="button-sort-account"
                      >
                        Account
                        <ArrowUpDown className="ml-1 h-3 w-3" />
                      </Button>
                    </TableHead>
                    <TableHead className="w-[130px] min-w-[130px] max-w-[130px]">Phone</TableHead>
                    <TableHead>Email</TableHead>
                    <TableHead>
                      <Button
                        variant="ghost"
                        size="sm"
                        className="h-8 p-0 font-medium"
                        onClick={() => handleSort("dateOfEvent")}
                        data-testid="button-sort-show-date"
                      >
                        Event Date
                        <ArrowUpDown className="ml-1 h-3 w-3" />
                      </Button>
                    </TableHead>
                    <TableHead>
                      <Button
                        variant="ghost"
                        size="sm"
                        className="h-8 p-0 font-medium"
                        onClick={() => handleSort("eventRate")}
                        data-testid="button-sort-amount"
                      >
                        Amount
                        <ArrowUpDown className="ml-1 h-3 w-3" />
                      </Button>
                    </TableHead>
                    <TableHead className="text-right">
                      <Button
                        variant="ghost"
                        size="sm"
                        className="h-8 p-0 font-medium"
                        onClick={() => handleSort("lateFee")}
                        data-testid="button-sort-late-fee"
                      >
                        Late Fee
                        <ArrowUpDown className="ml-1 h-3 w-3" />
                      </Button>
                    </TableHead>
                    <TableHead>
                      <Button
                        variant="ghost"
                        size="sm"
                        className="h-8 p-0 font-medium"
                        onClick={() => handleSort("delinquency")}
                        data-testid="button-sort-delinquency"
                      >
                        Status
                        <ArrowUpDown className="ml-1 h-3 w-3" />
                      </Button>
                    </TableHead>
                    <TableHead className="w-[80px] text-center">
                      <Button
                        variant="ghost"
                        size="sm"
                        className="h-8 p-0 font-medium"
                        onClick={() => handleSort("paid")}
                        data-testid="button-sort-paid"
                      >
                        Paid
                        <ArrowUpDown className="ml-1 h-3 w-3" />
                      </Button>
                    </TableHead>
                    <TableHead>Payment Details</TableHead>
                    <TableHead className="w-[60px]"></TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {visibleBillingInvoices.map((invoice) => (
                    <TableRow 
                      key={invoice.id} 
                      data-testid={`row-billing-invoice-${invoice.id}`}
                      className={cn(
                        "cursor-pointer hover-elevate",
                        invoice.status === "paid" && "bg-green-50 dark:bg-green-950/20",
                        selectedRowInvoice?.id === invoice.id && "bg-primary/10 dark:bg-primary/20",
                        selectedBillingIds.has(invoice.id) && "bg-primary/10"
                      )}
                      onClick={() => setSelectedRowInvoice(selectedRowInvoice?.id === invoice.id ? null : invoice)}
                      onDoubleClick={() => {
                        const email = invoice.clientEmail || "";
                        const phone = invoice.clientPhone || "";
                        setSelectedRowInvoice(invoice);
                        setEditForm({
                          accountName: invoice.accountName || "",
                          eventRate: invoice.eventRate || "",
                          billingNotes: invoice.billingNotes || "",
                          bookingDetails: invoice.bookingDetails || "",
                          emailAddress: email,
                          phoneNumber: phone,
                          status: invoice.status || "pending",
                          datePaid: invoice.datePaid
                            ? new Date(invoice.datePaid).toISOString().split("T")[0]
                            : "",
                        });
                        setOriginalContactInfo({
                          emailAddress: email,
                          phoneNumber: phone,
                        });
                        setEditDialogOpen(true);
                      }}
                    >
                      {isAdmin && (
                        <TableCell onClick={(e) => e.stopPropagation()}>
                          <Checkbox
                            checked={selectedBillingIds.has(invoice.id)}
                            onCheckedChange={(checked) => handleSelectBilling(invoice.id, !!checked)}
                            data-testid={`checkbox-select-billing-${invoice.id}`}
                          />
                        </TableCell>
                      )}
                      <TableCell className="font-medium truncate">
                        #{invoice.invoiceNumber}
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-2 flex-wrap">
                          <span>{invoice.accountName || "N/A"}</span>
                          {(() => {
                            const client = clientMap.get(invoice.clientId);
                            const cityState = [client?.city, client?.state].filter(Boolean).join(", ");
                            return cityState ? <span className="text-xs text-muted-foreground">({cityState})</span> : null;
                          })()}
                        </div>
                      </TableCell>
                      <TableCell className="w-[130px] min-w-[130px] max-w-[130px]">
                        {invoice.clientPhone ? (
                          <a 
                            href={`tel:${invoice.clientPhone}`} 
                            className="text-blue-600 dark:text-blue-400 hover:underline text-sm truncate block"
                            data-testid={`link-phone-${invoice.id}`}
                          >
                            {invoice.clientPhone}
                          </a>
                        ) : (
                          <span className="text-muted-foreground text-sm" data-testid={`text-phone-na-${invoice.id}`}>N/A</span>
                        )}
                      </TableCell>
                      <TableCell className="truncate max-w-[180px]" title={invoice.clientEmail || ""}>
                        {invoice.clientEmail ? (
                          <a 
                            href={`mailto:${invoice.clientEmail}`} 
                            className="text-blue-600 dark:text-blue-400 hover:underline text-sm truncate block"
                            data-testid={`link-email-${invoice.id}`}
                          >
                            {invoice.clientEmail}
                          </a>
                        ) : (
                          <span className="text-muted-foreground text-sm" data-testid={`text-email-na-${invoice.id}`}>N/A</span>
                        )}
                      </TableCell>
                      <TableCell className="truncate">{formatDate(invoice.dateOfEvent)}</TableCell>
                      <TableCell className="font-medium truncate">
                        {formatCurrency(Math.max(0, (Number(invoice.eventRate) || 0) - (Number(invoice.discount) || 0)))}
                      </TableCell>
                      <TableCell className="text-right truncate">
                        {invoice.lateFee && Number(invoice.lateFee) > 0 ? (
                          <span className="font-medium text-destructive">
                            {formatCurrency(invoice.lateFee)}
                          </span>
                        ) : (
                          <span className="text-muted-foreground">-</span>
                        )}
                      </TableCell>
                      <TableCell className="truncate">{getDelinquencyBadge(invoice)}</TableCell>
                      <TableCell className="text-center">
                        <Checkbox
                          checked={invoice.status === "paid"}
                          onCheckedChange={(checked) => handlePaidCheckChange(invoice, !!checked)}
                          data-testid={`checkbox-paid-${invoice.id}`}
                        />
                      </TableCell>
                      <TableCell className="truncate max-w-[150px]" title={invoice.paymentNotes || ""}>
                        {invoice.status === "paid" && invoice.paymentType && (
                          <div className="text-sm">
                            <div className="font-medium truncate">{invoice.paymentType}</div>
                            {invoice.datePaid && (
                              <div className="text-muted-foreground text-xs truncate">
                                {formatDate(invoice.datePaid)}
                              </div>
                            )}
                            {invoice.paymentNotes && (
                              <div className="text-muted-foreground text-xs italic truncate">
                                {invoice.paymentNotes}
                              </div>
                            )}
                          </div>
                        )}
                      </TableCell>
                      <TableCell>
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-8 w-8 text-muted-foreground hover:text-destructive"
                          onClick={(e) => {
                            e.stopPropagation();
                            setInvoiceToDelete(invoice);
                            setDeleteConfirmOpen(true);
                          }}
                          data-testid={`button-delete-invoice-${invoice.id}`}
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                  {/* Infinite scroll sentinel (Fix B) */}
                  {hasMoreBilling && (
                    <TableRow ref={billingLoadMoreRef}>
                      <TableCell colSpan={20} className="text-center text-xs text-muted-foreground py-2">
                        Loading more records…
                      </TableCell>
                    </TableRow>
                  )}
                  {!hasMoreBilling && filteredInvoices.length > 50 && (
                    <TableRow>
                      <TableCell colSpan={20} className="text-center text-xs text-muted-foreground py-1 bg-muted/20">
                        Showing all {filteredInvoices.length} records
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>

      <Dialog open={paymentDialogOpen} onOpenChange={(open) => !open && closePaymentDialog()}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Payment Details</DialogTitle>
            <DialogDescription>
              Record payment information for invoice #{selectedInvoice?.invoiceNumber}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="date-paid">Date Paid <span className="text-destructive">*</span></Label>
              <Input
                id="date-paid"
                type="date"
                value={datePaid}
                onChange={(e) => setDatePaid(e.target.value)}
                data-testid="input-date-paid"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="payment-type">Payment Type <span className="text-destructive">*</span></Label>
              <Select value={paymentType} onValueChange={setPaymentType}>
                <SelectTrigger data-testid="select-payment-type">
                  <SelectValue placeholder="Select payment type" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="Stripe">Stripe</SelectItem>
                  <SelectItem value="PayPal">PayPal</SelectItem>
                  <SelectItem value="ACH">ACH</SelectItem>
                  <SelectItem value="Cash">Cash</SelectItem>
                  <SelectItem value="Check">Check</SelectItem>
                  <SelectItem value="Zelle">Zelle</SelectItem>
                  <SelectItem value="Venmo">Venmo</SelectItem>
                  <SelectItem value="Other">Other</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label htmlFor="payment-notes">
                Notes {paymentType === "Other" && <span className="text-destructive">*</span>}
              </Label>
              <Textarea
                id="payment-notes"
                value={paymentNotes}
                onChange={(e) => setPaymentNotes(e.target.value)}
                placeholder={paymentType === "Other" ? "Required for 'Other' payment type..." : "Optional notes..."}
                data-testid="input-payment-notes"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={closePaymentDialog} data-testid="button-cancel-payment">
              Cancel
            </Button>
            <Button
              onClick={handlePaymentSubmit}
              disabled={!datePaid || !paymentType || (paymentType === "Other" && !paymentNotes.trim()) || updateInvoiceMutation.isPending}
              data-testid="button-confirm-payment"
            >
              {updateInvoiceMutation.isPending ? (
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              ) : null}
              Save Payment
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Invoice Dialog */}
      <Dialog open={editDialogOpen} onOpenChange={setEditDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Edit Invoice #{selectedRowInvoice?.invoiceNumber}</DialogTitle>
            <DialogDescription>
              Update invoice details for {selectedRowInvoice?.accountName}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="edit-account-name">Account Name</Label>
              <Input
                id="edit-account-name"
                value={editForm.accountName}
                onChange={(e) => setEditForm(prev => ({ ...prev, accountName: e.target.value }))}
                data-testid="input-edit-account-name"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="edit-show-rate">Event Rate</Label>
              <Input
                id="edit-show-rate"
                type="number"
                step="0.01"
                value={editForm.eventRate}
                onChange={(e) => setEditForm(prev => ({ ...prev, eventRate: e.target.value }))}
                data-testid="input-edit-show-rate"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="edit-status">Status</Label>
              <Select
                value={editForm.status}
                onValueChange={(value) => setEditForm(prev => ({ ...prev, status: value }))}
              >
                <SelectTrigger id="edit-status" data-testid="select-edit-status">
                  <SelectValue placeholder="Select status" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="Upcoming">Upcoming</SelectItem>
                  <SelectItem value="pending">Pending</SelectItem>
                  <SelectItem value="paid">Paid</SelectItem>
                  <SelectItem value="overdue">Overdue</SelectItem>
                  <SelectItem value="cancelled">Cancelled</SelectItem>
                </SelectContent>
              </Select>
            </div>
            {selectedRowInvoice?.status === "paid" && (
              <div className="space-y-2">
                <Label htmlFor="edit-date-paid">Date Paid</Label>
                {canEditDatePaid ? (
                  <Input
                    id="edit-date-paid"
                    type="date"
                    value={editForm.datePaid}
                    onChange={(e) => setEditForm(prev => ({ ...prev, datePaid: e.target.value }))}
                    data-testid="input-edit-date-paid"
                  />
                ) : (
                  <div
                    className="h-9 px-3 flex items-center text-sm rounded-md border bg-muted/30 text-muted-foreground"
                    data-testid="text-edit-date-paid-readonly"
                  >
                    {editForm.datePaid || "—"}
                  </div>
                )}
              </div>
            )}
            <div className="space-y-2">
              <Label htmlFor="edit-billing-notes">Billing Notes</Label>
              <Textarea
                id="edit-billing-notes"
                value={editForm.billingNotes}
                onChange={(e) => setEditForm(prev => ({ ...prev, billingNotes: e.target.value }))}
                rows={2}
                data-testid="input-edit-billing-notes"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="edit-booking-details">Booking Details</Label>
              <Textarea
                id="edit-booking-details"
                value={editForm.bookingDetails}
                onChange={(e) => setEditForm(prev => ({ ...prev, bookingDetails: e.target.value }))}
                rows={3}
                data-testid="input-edit-booking-details"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="edit-email" className="flex items-center gap-2">
                <Mail className="w-4 h-4" />
                Email Address
              </Label>
              <Input
                id="edit-email"
                type="email"
                value={editForm.emailAddress}
                onChange={(e) => setEditForm(prev => ({ ...prev, emailAddress: e.target.value }))}
                data-testid="input-edit-email"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="edit-phone" className="flex items-center gap-2">
                <Phone className="w-4 h-4" />
                Phone Number
              </Label>
              <Input
                id="edit-phone"
                type="tel"
                value={editForm.phoneNumber}
                onChange={(e) => setEditForm(prev => ({ ...prev, phoneNumber: e.target.value }))}
                data-testid="input-edit-phone"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditDialogOpen(false)} data-testid="button-cancel-edit">
              Cancel
            </Button>
            <Button
              onClick={() => {
                if (selectedRowInvoice) {
                  const rateVal = parseFloat(editForm.eventRate);
                  if (!editForm.eventRate || editForm.eventRate.trim() === "" || rateVal <= 0) {
                    toast({
                      title: "Invalid Event Rate",
                      description: "Event Rate can only be set to $0 by canceling the invoice.",
                      variant: "destructive",
                    });
                    return;
                  }
                  const emailChanged = editForm.emailAddress !== originalContactInfo.emailAddress;
                  const phoneChanged = editForm.phoneNumber !== originalContactInfo.phoneNumber;
                  
                  if (emailChanged || phoneChanged) {
                    setContactChangeConfirmOpen(true);
                  } else {
                    updateInvoiceMutation.mutate({
                      id: selectedRowInvoice.id,
                      updates: {
                        accountName: editForm.accountName,
                        eventRate: editForm.eventRate,
                        billingNotes: editForm.billingNotes,
                        bookingDetails: editForm.bookingDetails,
                        status: editForm.status,
                        ...(selectedRowInvoice.status === "paid" && canEditDatePaid
                          ? { datePaid: editForm.datePaid }
                          : {}),
                      },
                    }, {
                      onSuccess: () => {
                        setEditDialogOpen(false);
                        setSelectedRowInvoice(null);
                        toast({
                          title: "Invoice Updated",
                          description: "Changes have been saved.",
                        });
                      },
                    });
                  }
                }
              }}
              disabled={updateInvoiceMutation.isPending || updateClientMutation.isPending}
              data-testid="button-save-edit"
            >
              {(updateInvoiceMutation.isPending || updateClientMutation.isPending) ? (
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              ) : null}
              Save Changes
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Contact Change Confirmation Dialog */}
      <AlertDialog open={contactChangeConfirmOpen} onOpenChange={setContactChangeConfirmOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Update Contact Information?</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to change the {editForm.emailAddress !== originalContactInfo.emailAddress && editForm.phoneNumber !== originalContactInfo.phoneNumber ? "email address and phone number" : editForm.emailAddress !== originalContactInfo.emailAddress ? "email address" : "phone number"}? This will change the contact information for all records associated with this client.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="button-cancel-contact-change">Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => {
                if (selectedRowInvoice) {
                  const rateVal = parseFloat(editForm.eventRate);
                  if (!editForm.eventRate || editForm.eventRate.trim() === "" || rateVal <= 0) {
                    toast({
                      title: "Invalid Event Rate",
                      description: "Event Rate can only be set to $0 by canceling the invoice.",
                      variant: "destructive",
                    });
                    setContactChangeConfirmOpen(false);
                    return;
                  }
                  const client = clients.find(c => c.accountName === selectedRowInvoice.accountName);
                  
                  updateInvoiceMutation.mutate({
                    id: selectedRowInvoice.id,
                    updates: {
                      accountName: editForm.accountName,
                      eventRate: editForm.eventRate,
                      billingNotes: editForm.billingNotes,
                      bookingDetails: editForm.bookingDetails,
                      status: editForm.status,
                      ...(selectedRowInvoice.status === "paid" && canEditDatePaid
                        ? { datePaid: editForm.datePaid }
                        : {}),
                    },
                  }, {
                    onSuccess: () => {
                      if (client) {
                        updateClientMutation.mutate({
                          id: client.id,
                          updates: {
                            emailAddress: editForm.emailAddress,
                            phoneNumber: editForm.phoneNumber,
                          },
                        }, {
                          onSuccess: () => {
                            setEditDialogOpen(false);
                            setSelectedRowInvoice(null);
                            setContactChangeConfirmOpen(false);
                            toast({
                              title: "Record Updated",
                              description: "Invoice and contact information have been saved.",
                            });
                          },
                        });
                      } else {
                        setEditDialogOpen(false);
                        setSelectedRowInvoice(null);
                        setContactChangeConfirmOpen(false);
                        toast({
                          title: "Invoice Updated",
                          description: "Changes have been saved. Client record not found to update contact info.",
                        });
                      }
                    },
                  });
                }
              }}
              data-testid="button-confirm-contact-change"
            >
              Yes, Update Contact Info
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog open={deleteConfirmOpen} onOpenChange={setDeleteConfirmOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Invoice</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete invoice #{invoiceToDelete?.invoiceNumber}? 
              This invoice will be moved to the deleted items and can be restored within 7 days.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="button-cancel-delete">Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => {
                if (invoiceToDelete) {
                  deleteInvoiceMutation.mutate(invoiceToDelete.id);
                }
              }}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              data-testid="button-confirm-delete"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Bulk Edit Dialog */}
      <Dialog open={isBulkEditOpen} onOpenChange={setIsBulkEditOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Bulk Edit {selectedBillingIds.size} Invoices</DialogTitle>
            <DialogDescription>Select a field and value to apply to all selected invoices.</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label>Field to Edit</Label>
              <Select value={bulkEditField} onValueChange={(value) => { setBulkEditField(value); setBulkEditValue(""); }}>
                <SelectTrigger data-testid="select-bulk-field-billing">
                  <SelectValue placeholder="Select field..." />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="status">Status</SelectItem>
                  <SelectItem value="bookingDetails">Booking Details</SelectItem>
                  <SelectItem value="billingNotes">Billing Notes</SelectItem>
                </SelectContent>
              </Select>
            </div>
            {bulkEditField === "status" && (
              <div className="space-y-2">
                <Label>New Status</Label>
                <Select value={bulkEditValue} onValueChange={setBulkEditValue}>
                  <SelectTrigger data-testid="select-bulk-status-billing">
                    <SelectValue placeholder="Select status..." />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="unpaid">Unpaid</SelectItem>
                    <SelectItem value="pending">Pending</SelectItem>
                    <SelectItem value="cancelled">Cancelled</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            )}
            {(bulkEditField === "bookingDetails" || bulkEditField === "billingNotes") && (
              <div className="space-y-2">
                <Label>New Value</Label>
                <Input
                  value={bulkEditValue}
                  onChange={(e) => setBulkEditValue(e.target.value)}
                  placeholder={bulkEditField === "bookingDetails" ? "Enter booking details..." : "Enter billing notes..."}
                  data-testid="input-bulk-value-billing"
                />
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsBulkEditOpen(false)} data-testid="button-cancel-bulk-billing">
              Cancel
            </Button>
            <Button 
              onClick={handleBulkEdit} 
              disabled={!bulkEditField || (bulkEditField === "status" && !bulkEditValue) || bulkEditMutation.isPending}
              data-testid="button-apply-bulk-billing"
            >
              {bulkEditMutation.isPending ? "Applying..." : "Apply Changes"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Email Dialog */}
      <Dialog open={isEmailDialogOpen} onOpenChange={(open) => {
        setIsEmailDialogOpen(open);
        if (!open) setEmailAttachments([]);
      }}>
        <DialogContent className="sm:max-w-[500px]">
          <DialogHeader>
            <DialogTitle>Send Email to {selectedRowInvoice?.accountName}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>To</Label>
              <Input value={selectedRowInvoice?.clientEmail || ""} disabled className="bg-muted" />
            </div>
            <CcEmailInput value={emailCc} onChange={setEmailCc} data-testid="input-email-cc" />
            <div className="space-y-2">
              <Label htmlFor="emailSubject">Subject</Label>
              <Input
                id="emailSubject"
                value={emailSubject}
                onChange={(e) => setEmailSubject(e.target.value)}
                placeholder="Enter subject..."
                data-testid="input-email-subject"
              />
            </div>
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <Label htmlFor="emailBody">Message</Label>
                <VoiceToTextButton onTranscript={(t) => emailBodyEditorRef.current?.insertAtCursor(t)} />
              </div>
              <RichTextEditor
                ref={emailBodyEditorRef}
                value={emailBody}
                onChange={setEmailBody}
                placeholder="Write your message..."
                minHeight="150px"
              />
            </div>
            <EmailAttachmentsField
              value={emailAttachments}
              onChange={setEmailAttachments}
              testIdPrefix="billing-email-attachments"
            />
            <div className="flex gap-2">
              <Button
                variant="outline"
                onClick={() => setIsEmailScheduleOpen(true)}
                disabled={!emailSubject || !emailBody || sendEmailMutation.isPending}
                className="flex-1"
                data-testid="button-schedule-billing-email"
              >
                <Clock className="w-4 h-4 mr-2" />
                Schedule send
              </Button>
              <Button
                onClick={() => {
                  if (selectedRowInvoice?.clientEmail && emailSubject && emailBody) {
                    sendEmailMutation.mutate({
                      to: selectedRowInvoice.clientEmail,
                      subject: emailSubject,
                      body: emailBody,
                      cc: emailCc.trim() || undefined,
                      attachments: emailAttachments,
                    });
                  }
                }}
                disabled={!emailSubject || !emailBody || sendEmailMutation.isPending}
                className="flex-1"
                data-testid="button-send-email"
              >
                {sendEmailMutation.isPending ? (
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                ) : (
                  <Send className="w-4 h-4 mr-2" />
                )}
                Send Email
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      <ScheduledEmailDialog
        open={isEmailScheduleOpen}
        onOpenChange={setIsEmailScheduleOpen}
        sourceType="one_off"
        recipientSummary={selectedRowInvoice?.clientEmail ? `Schedule email to ${selectedRowInvoice.clientEmail}` : undefined}
        subjectSummary={emailSubject}
        buildPayload={() => {
          if (!selectedRowInvoice?.clientEmail || !emailSubject || !emailBody) return null;
          return {
            to: selectedRowInvoice.clientEmail,
            cc: emailCc.trim() || undefined,
            subject: emailSubject,
            htmlBody: emailBody,
            attachments: emailAttachments.length > 0
              ? emailAttachments.map((a) => ({
                  filename: a.filename,
                  contentBase64: a.contentBase64,
                  contentType: a.contentType,
                }))
              : undefined,
            invoiceId: selectedRowInvoice?.id,
          };
        }}
        onScheduled={() => setIsEmailDialogOpen(false)}
      />

      {/* Log Call Dialog */}
      <Dialog open={isLogCallDialogOpen} onOpenChange={setIsLogCallDialogOpen}>
        <DialogContent className="sm:max-w-[500px]">
          <DialogHeader>
            <DialogTitle>Log Call for {selectedRowInvoice?.accountName}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="callNotes">Call Notes</Label>
              <Textarea
                id="callNotes"
                value={logCallNotes}
                onChange={(e) => setLogCallNotes(e.target.value)}
                placeholder="Describe the call..."
                rows={6}
                data-testid="input-call-notes"
              />
            </div>
            <Button
              onClick={() => {
                if (selectedRowInvoice?.id && logCallNotes.trim()) {
                  createActivityMutation.mutate({
                    invoiceId: selectedRowInvoice.id,
                    type: "call",
                    notes: logCallNotes,
                  });
                }
              }}
              disabled={!logCallNotes.trim() || createActivityMutation.isPending}
              className="w-full"
              data-testid="button-save-call"
            >
              {createActivityMutation.isPending ? (
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              ) : (
                <Phone className="w-4 h-4 mr-2" />
              )}
              Save Call Log
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Set Reminder Dialog */}
      <Dialog open={isSetReminderDialogOpen} onOpenChange={setIsSetReminderDialogOpen}>
        <DialogContent className="sm:max-w-[400px]">
          <DialogHeader>
            <DialogTitle>Set Reminder for {selectedRowInvoice?.accountName}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="reminderDate">Due Date</Label>
              <Input
                id="reminderDate"
                type="date"
                value={reminderDate}
                onChange={(e) => setReminderDate(e.target.value)}
                data-testid="input-reminder-date"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="reminderNote">Note (optional)</Label>
              <Textarea
                id="reminderNote"
                value={reminderNote}
                onChange={(e) => setReminderNote(e.target.value)}
                placeholder="Add a note for this reminder..."
                rows={3}
                data-testid="input-reminder-note"
              />
            </div>
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setIsSetReminderDialogOpen(false)}
              data-testid="button-cancel-reminder"
            >
              Cancel
            </Button>
            <Button
              onClick={() => {
                if (selectedRowInvoice?.id && reminderDate) {
                  createReminderMutation.mutate({
                    invoiceId: selectedRowInvoice.id,
                    dueDate: reminderDate,
                    note: reminderNote,
                  });
                }
              }}
              disabled={!reminderDate || createReminderMutation.isPending}
              data-testid="button-save-reminder"
            >
              {createReminderMutation.isPending ? "Saving..." : "Save Reminder"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* View Details Dialog */}
      <Dialog open={isViewDetailsDialogOpen} onOpenChange={setIsViewDetailsDialogOpen}>
        <DialogContent className="sm:max-w-[600px] max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Activity Timeline - {selectedRowInvoice?.accountName}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            {selectedRowInvoice && (
              <div className="p-4 bg-muted rounded-lg">
                <h4 className="font-medium mb-2">Invoice Information</h4>
                <div className="grid grid-cols-2 gap-2 text-sm">
                  <div><span className="text-muted-foreground">Invoice #:</span> {selectedRowInvoice.invoiceNumber || "N/A"}</div>
                  <div><span className="text-muted-foreground">Account:</span> {selectedRowInvoice.accountName || "N/A"}</div>
                  <div><span className="text-muted-foreground">Phone:</span> {selectedRowInvoice.clientPhone || "N/A"}</div>
                  <div><span className="text-muted-foreground">Email:</span> {selectedRowInvoice.clientEmail || "N/A"}</div>
                  <div><span className="text-muted-foreground">Status:</span> {selectedRowInvoice.status}</div>
                  <div><span className="text-muted-foreground">Event Rate:</span> ${selectedRowInvoice.eventRate || "0"}</div>
                </div>
              </div>
            )}
            
            <div>
              <h4 className="font-medium mb-3">Activity History</h4>
              {isLoadingActivities ? (
                <p className="text-sm text-muted-foreground">Loading activity history...</p>
              ) : invoiceActivities.length === 0 ? (
                <p className="text-sm text-muted-foreground">No activity history found.</p>
              ) : (
                <div className="space-y-3">
                  {invoiceActivities.map((activity: any) => {
                    const isPayment = activity.type === "payment" || activity.type === "payment_received";
                    const label = isPayment ? "Marked Paid"
                      : activity.type === "call" ? "Call"
                      : activity.type === "email" || activity.type === "email_sent" ? "Email"
                      : activity.type === "review" ? "Event Review"
                      : activity.type.replace(/_/g, " ").replace(/\b\w/g, (c: string) => c.toUpperCase());
                    return (
                      <div key={activity.id} className="flex gap-3 p-3 border rounded-lg">
                        <div className="flex-shrink-0 mt-0.5">
                          {activity.type === "call" && <Phone className="w-5 h-5 text-blue-500" />}
                          {(activity.type === "email" || activity.type === "email_sent") && <Mail className="w-5 h-5 text-green-500" />}
                          {activity.type === "note" && <Pencil className="w-5 h-5 text-orange-500" />}
                          {isPayment && <CheckCircle2 className="w-5 h-5 text-green-600" />}
                        </div>
                        <div className="flex-1">
                          <div className="flex justify-between items-start">
                            <span className="font-medium">{label}</span>
                            <span className="text-xs text-muted-foreground">
                              {new Date(activity.createdAt).toLocaleString()}
                            </span>
                          </div>
                          {activity.notes && (
                            <div className="mt-1">
                              <ExpandableText text={activity.notes} className="text-sm text-muted-foreground" />
                            </div>
                          )}
                          {activity.createdByName && (
                            <p className="text-xs text-muted-foreground mt-1">By: {activity.createdByName}</p>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </div>
        </DialogContent>
      </Dialog>
        </TabsContent>
      </Tabs>

      <PaymentEmailModal
        open={paymentEmailModalOpen}
        onClose={() => { setPaymentEmailModalOpen(false); setJustPaidInvoice(null); }}
        invoiceId={justPaidInvoice?.id ?? null}
        clientId={justPaidInvoice?.clientId ?? null}
        clientEmail={justPaidInvoice?.clientEmail ?? justPaidInvoice?.billingEmail ?? null}
        clientName={justPaidInvoice?.accountName ?? null}
        invoiceNumber={justPaidInvoice?.invoiceNumber ?? null}
      />
    </div>
  );
}
